import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { FilterLogicalOperator, MetricFilterOperator, Unit } from 'webapp/enums/enums';
import { equal, uniq } from 'webapp/utils/common';
import { hashCode } from 'webapp/utils/number';

import type { FilterItem, Metric } from 'webapp/models-custom';
import type { MetricSetupBaseProps, OnChangeFn } from 'webapp/models-custom/metric-setup-base';
import MetricSetupBase from 'webapp/models-custom/metric-setup-base';

export interface Props extends MetricSetupBaseProps {
	allowAllUnits?: boolean;
	allowAllDimensionValues?: boolean;
}

export default class MetricSetup extends MetricSetupBase {
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked allowAllDimensionValues = true;
	@tracked allowAllUnits = true;

	constructor(props: Props = {}, onChange?: OnChangeFn) {
		super(props);

		if (typeof props.allowAllUnits !== 'undefined') {
			this.allowAllUnits = props.allowAllUnits;
		}

		if (typeof props.allowAllDimensionValues !== 'undefined') {
			this.allowAllDimensionValues = props.allowAllDimensionValues;
		}

		// metric will be automatically read from key and spaceAccessId bcos its from the same space
		this.afterMetricSet(props, onChange);
	}

	// --------- KEY + SAID + METRIC ----------

	@cached
	get metric(): Metric | null {
		let metric: Metric | null = null;
		if (this.key) {
			metric = this.mmMetricsService.getMetricForKeyAndSpaceAccessId(
				this.key,
				this.spaceAccessId,
			);
		}
		return metric;
	}

	get sourceType() {
		return this.metric?.sourceType;
	}

	get source() {
		return this.metric?.source;
	}

	get sourceTypeKeys() {
		// for tracking
		const sourceTypeKey = this.metric?.dataSourceTypeKey;
		return sourceTypeKey ? [sourceTypeKey] : [];
	}

	// -------- UNITS  --------

	get defaultUnit(): string {
		return this.allowAllUnits ? Unit.ALL : Unit.EMPTY;
	}

	// --------- MISC ----------

	@cached
	get uniqueSetupDataKey(): string {
		const filtersHash = hashCode(JSON.stringify(this.filtersData));
		return `${this.key}-${this.spaceAccessId}-${this.dimensions.join(',')}-${filtersHash}`;
	}

	get selectedMetricTitle(): string {
		const attributeNames = this.attributeNamesFromBasicFilters.join(', ');
		return `${this.metric?.name}${attributeNames ? ` / ${attributeNames}` : ''}`;
	}

	get selectedMetricTitleWithUnit(): string {
		let title = this.selectedMetricTitle;
		if (this.specificSelectedUnit) {
			title += ` / ${this.specificSelectedUnit}`;
		}
		return title;
	}

	get selectedAttributesAndUnit(): string {
		const attributeNames = this.attributeNamesFromBasicFilters.join(', ');
		return `${attributeNames}${this.specificSelectedUnit ? ` / ${this.specificSelectedUnit}` : ''}`;
	}

	get isOneDimensionSelected(): boolean {
		return this.dimensions.length === 1;
	}

	get isDimensionSelectedButNotAllowed(): boolean {
		return this.isDimensionSelected && !this.allowDimensions;
	}

	get outputAttributesCount(): number {
		// not a precise logic!
		// used in determining if we have multiple attribute outputs
		// e.g. for chart labels (name of attribute vs name of metric)

		if (!this.isDimensionSelected) {
			// no dimension selected
			return 0;
		}
		const filterGroup = this.dimensionFilterGroup;
		if (!filterGroup) {
			// dimensions selected, no filters
			return Infinity;
		}
		const allItems = filterGroup.filterItems;

		if (filterGroup.logicalOperator === FilterLogicalOperator.AND) {
			// multi-dim AND fan-out is multiplicative: product of per-dim IS value counts
			let total = 1;
			for (const dim of this.dimensions) {
				const isItems = allItems.filter((item) => {
					return item.operator === MetricFilterOperator.ANY_OF && item.field === dim;
				});
				if (!isItems.length) {
					// dim is unconstrained by IS, so we cannot determine the output count
					return Infinity;
				}
				// several IS on one dim are read as one list (framework merges them), so count their distinct values
				total *= uniq(isItems.flatMap((item) => item.values)).length;
			}
			return total;
		}

		if (this.isMultipleDimensionsSelected) {
			// OR across multiple dimensions has no clean upper bound
			return Infinity;
		}

		const selectedDimension = this.dimensions[0];
		const isItems = allItems.filter((item) => {
			return (
				item.operator === MetricFilterOperator.ANY_OF && item.field === selectedDimension
			);
		});
		if (allItems.length === isItems.length) {
			// not precise really, bcos we could have IS xy OR IS xz -> output 3, not 4
			return isItems.reduce((acc, item) => {
				return acc + item.values.length;
			}, 0);
		}
		// cannot determine the output count
		return Infinity;
	}

	get isOutputMultipleAttributes(): boolean {
		// this means if the result *could* be multiple attributes (taking into account adv filters)
		if (this.allowDimensions) {
			if (this.isMultipleDimensionsSelected) {
				return true;
			}
			if (this.isOneDimensionSelected) {
				const outputAttributesCount = this.outputAttributesCount;
				return outputAttributesCount > 1;
			}
		}
		return false;
	}

	get isOutputMultipleUnits(): boolean {
		return this.hasUnits && !this.unitName;
	}

	get isOutputMultiples(): boolean {
		return this.isOutputMultipleAttributes || this.isOutputMultipleUnits;
	}

	get isOutputUnlimitedMultiples(): boolean {
		return (
			(this.allowDimensions && this.isMultipleDimensionsSelected) ||
			(this.allowDimensions &&
				this.isOneDimensionSelected &&
				this.outputAttributesCount === Infinity) ||
			this.isOutputMultipleUnits
		);
	}

	get isSingleOutput() {
		return !this.isOutputMultiples;
	}

	get isFilledSingleOutput() {
		return !!this.metric && this.isSingleOutput;
	}

	@cached
	get isFiltersInvalid() {
		const metricData = this.metricData;
		return this.filterGroups.some((group) => {
			return group.filterItems.some((item) => {
				if (item.isTypeDimension) {
					const dimension = item.field;
					const limitations = this.mmMetricsService.getCachedOperatorLimitations([
						Object.assign({ dimensions: [dimension] }, metricData),
					]);
					return (
						limitations &&
						!this.allowedOperatorsForField(dimension, limitations).includes(
							item.operator,
						)
					);
				} else {
					return false;
				}
			});
		});
	}

	// mirrors framework MetricSetup::isSimpleFilter: one group of "Is" / "Is not" filters on the breakdown
	// ignoredItem: the filter being edited (its operator is the one being chosen)
	hasOnlySimpleFilters(ignoredItem?: FilterItem): boolean {
		const [group, ...otherGroups] = this.filterGroups;
		if (!group) {
			return true;
		}
		return (
			!otherGroups.length &&
			group.filterItems.every(
				(item) =>
					item === ignoredItem ||
					(item.isTypeDimension &&
						[MetricFilterOperator.ANY_OF, MetricFilterOperator.NONE_OF].includes(
							item.operator,
						) &&
						this.dimensions.includes(item.field)),
			)
		);
	}

	// Horizon: "Is" / "Is not" on the setup's own breakdown is applied at read time, so it's allowed even when
	// Horizon offers no filter options for the field (and the loaded limitations are empty),
	// but only while the filters stay simple
	allowedOperatorsForField(
		field: string,
		limitations: MetricFilterOperator[],
		ignoredItem?: FilterItem,
	): MetricFilterOperator[] {
		if (
			!this.metric?.isTypeHorizon ||
			!this.dimensions.includes(field) ||
			!this.hasOnlySimpleFilters(ignoredItem)
		) {
			return limitations;
		}
		const breakdownOperators = [MetricFilterOperator.ANY_OF, MetricFilterOperator.NONE_OF];
		return [...breakdownOperators.filter((op) => !limitations.includes(op)), ...limitations];
	}

	isMatchingMetricDataFromMetricSetup(metricSetup: MetricSetup) {
		return this.key === metricSetup.key && this.spaceAccessId === metricSetup.spaceAccessId;
	}

	isMatchingDimensionsFromMetricSetup(metricSetup: MetricSetup) {
		return equal(this.dimensions, metricSetup.dimensions);
	}

	isMatchingMetricSetup(metricSetup: MetricSetup) {
		return (
			this.isMatchingMetricDataFromMetricSetup(metricSetup) &&
			this.isMatchingDimensionsFromMetricSetup(metricSetup) &&
			this.isMatchingFiltersFromMetricSetup(metricSetup)
		);
	}

	isMatchingFiltersFromMetricSetup(metricSetup: MetricSetup) {
		return this.filterGroups.every((group, index) => {
			const matchingGroup = metricSetup.filterGroups[index];
			if (!matchingGroup) {
				return false;
			}
			return group.isEqual(matchingGroup);
		});
	}

	createCopy() {
		return new MetricSetup(this.metricSetupData || undefined);
	}
}
